import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { TEST_DEFINITIONS, DEFAULT_TEST_ID, getDefinition } from './definitions';
import { createCalculationWorker } from './workerFactory';

const panel = { padding: '1rem', border: '1px solid var(--ni-border-soft)', borderRadius: 10, marginBottom: '1rem' };
const muted = { color: 'var(--ni-text-muted)', fontSize: '.9rem', lineHeight: 1.5 };
const field = { width: '100%', boxSizing: 'border-box', padding: '.6rem', borderRadius: 6,
  border: '1px solid var(--ni-border-soft)', background: 'var(--ni-panel)', color: 'var(--ni-text)' };
const initialInputs = definition => Object.fromEntries(definition.inputs.map(input => [input.key, input.type === 'number' ? String(input.default) : input.default]));
const displayValue = value => typeof value === 'boolean' ? value ? 'Yes' : 'No' : ['number', 'string'].includes(typeof value) ? String(value) : 'Unavailable';
const stopWorker = worker => {
  if (!worker) return;
  worker.onmessage = null; worker.onerror = null; worker.onmessageerror = null; worker.terminate();
};

function readInputs(definition, values) {
  return Object.fromEntries(definition.inputs.map(input => {
    if (input.type === 'boolean') return [input.key, values[input.key] === true];
    const text = String(values[input.key] ?? '').trim(), value = Number(text);
    if (!text || !Number.isFinite(value)) throw new Error(`Enter a finite number for ${input.label}.`);
    if ((input.integer && !Number.isSafeInteger(value)) || (input.min !== undefined && value < input.min) || (input.max !== undefined && value > input.max)) throw new Error(`${input.label} is outside the allowed range.`);
    return [input.key, value];
  }));
}

function ResultView({ result }) {
  return <section style={panel} aria-label="Simulated result">
    <h2>Simulated outcome: {result.outcome === 'pass' ? 'Pass' : 'Fail'}</h2>
    <p>{result.summary}</p>
    <p style={muted}>{result.definition.label} · Definition version {result.definition.version} · Synthetic data</p>
    {!!result.results?.length && <div className="ni-table-wrap"><table className="ni-table">
      <thead><tr><th>Check</th><th>Simulated value</th><th>Outcome</th></tr></thead>
      <tbody>{result.results.map(item => <tr key={item.key}><td>{item.label}</td><td style={{ overflowWrap: 'anywhere' }}>{displayValue(item.value)}{item.unit ? ` ${item.unit}` : ''}</td>
        <td>{item.passed === true ? 'Pass' : item.passed === false ? 'Fail' : 'Reference value'}</td></tr>)}</tbody>
    </table></div>}
    {result.referenceComparison && <p>Calculation reference: {result.referenceComparison.matched ? 'Matches' : 'Differs'} · Numbers checked: {result.referenceComparison.checkedNumbers} · Exact table values: {result.referenceComparison.exactLutValues}</p>}
    {(result.tables || []).map(table => <details key={table.key} style={{ marginTop: '.8rem' }}><summary>{table.label} · {table.rows.length} rows</summary>
      <div className="ni-table-wrap"><table className="ni-table"><thead><tr>{table.columns.map((column, index) => <th key={index}>{column}</th>)}</tr></thead>
        <tbody>{table.rows.map((values, row) => <tr key={row}>{values.map((value, column) => <td key={column}>{displayValue(value)}</td>)}</tr>)}</tbody></table></div>
    </details>)}
    <p style={muted}>This page holds a local simulation result. It does not release a test or record a production outcome.</p>
  </section>;
}

export default function TestRuntimeLab() {
  const [definitionId, setDefinitionId] = useState(DEFAULT_TEST_ID);
  const definition = getDefinition(definitionId);
  const [inputs, setInputs] = useState(() => initialInputs(getDefinition(DEFAULT_TEST_ID)));
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [elapsed, setElapsed] = useState(null);
  const [responseChecks, setResponseChecks] = useState(0);
  const [checksDuringRun, setChecksDuringRun] = useState(0);
  const generation = useRef(0);
  const active = useRef(null);
  const [capabilities] = useState(() => ({ worker: typeof window.Worker === 'function', secure: window.isSecureContext === true,
    serial: 'serial' in navigator, usb: 'usb' in navigator, bluetooth: 'bluetooth' in navigator }));
  const running = status === 'running';
  useEffect(() => () => { generation.current += 1; stopWorker(active.current?.worker); active.current = null; }, []);

  const cancel = () => {
    const attempt = active.current;
    generation.current += 1; active.current = null; stopWorker(attempt?.worker);
    if (attempt) setElapsed(Math.max(0, performance.now() - attempt.startedAt));
    setStatus('cancelled'); setResult(null); setError('');
  };
  const selectDefinition = id => {
    generation.current += 1; stopWorker(active.current?.worker); active.current = null;
    setDefinitionId(id); setInputs(initialInputs(getDefinition(id))); setStatus('idle'); setResult(null); setError(''); setElapsed(null); setChecksDuringRun(0);
  };
  const updateInput = (key, value) => {
    generation.current += 1; stopWorker(active.current?.worker); active.current = null;
    setInputs(previous => ({ ...previous, [key]: value })); setStatus('idle'); setResult(null); setError(''); setElapsed(null); setChecksDuringRun(0);
  };
  const start = event => {
    event.preventDefault();
    let values;
    try { values = readInputs(definition, inputs); } catch (failure) { setError(failure.message); return; }
    const current = ++generation.current, startedAt = performance.now(), requestId = `browser-lab-${current}`;
    stopWorker(active.current?.worker); active.current = null;
    setStatus('running'); setResult(null); setError(''); setElapsed(null); setChecksDuringRun(0);
    let worker;
    const finish = (nextStatus, nextResult, message = '') => {
      if (generation.current !== current || active.current?.requestId !== requestId) return;
      active.current = null; stopWorker(worker);
      setElapsed(Math.max(0, performance.now() - startedAt)); setStatus(nextStatus); setResult(nextResult); setError(message);
    };
    try {
      worker = createCalculationWorker(); active.current = { worker, requestId, startedAt };
      worker.onmessage = message => {
        const data = message.data;
        if (generation.current !== current || active.current?.requestId !== requestId || data?.requestId !== requestId) return;
        if (data.type === 'error') { finish('error', null, data.error?.message || 'The simulated calculation failed.'); return; }
        if (data.type !== 'result') return;
        const next = data.result;
        if (next?.schemaVersion !== 'qms-browser-test-result/1' || next.requestId !== requestId || next.definition?.id !== definition.id
          || next.definition.version !== definition.version || next.mode !== 'simulation' || next.provenance?.source !== 'simulation'
          || next.provenance.hardwareUsed !== false || next.provenance.persisted !== false || next.status !== 'completed' || !['pass', 'fail'].includes(next.outcome)) {
          finish('error', null, 'The calculation returned an unexpected result. Run the simulation again.'); return;
        }
        finish('completed', next);
      };
      worker.onerror = failure => { failure.preventDefault?.(); finish('error', null, 'The background calculation stopped unexpectedly. Try the simulation again.'); };
      worker.onmessageerror = () => finish('error', null, 'The calculation response could not be read.');
      worker.postMessage({ type: 'run', requestId, definitionId: definition.id, inputs: values });
    } catch (failure) {
      stopWorker(worker); active.current = null;
      setElapsed(Math.max(0, performance.now() - startedAt)); setStatus('error'); setError(`The background calculation could not start. ${failure.message}`);
    }
  };

  return <div>
    <Link to="/quality/test-tools">← Tools and software</Link>
    <h1>Browser test lab</h1>
    <p style={muted}>Try a shared test format using synthetic inputs. Choose an inspection, an electrical check or an optical calculation. Results stay on this page.</p>
    <p style={{ color: 'var(--ni-green)', fontWeight: 700 }}>Simulation only · No hardware access · No production records saved</p>
    <section style={panel} aria-label="Browser capabilities"><h2>Browser capabilities</h2>
      <p style={muted}>Availability checks only. This page does not request device access.</p>
      <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(170px,1fr) 1fr', gap: '.5rem' }}>
        {Object.entries({ worker: 'Background calculations', secure: 'Secure browser context', serial: 'Serial support', usb: 'USB support', bluetooth: 'Bluetooth support' }).map(([key, label]) => <React.Fragment key={key}><dt>{label}</dt><dd style={{ margin: 0 }}>{capabilities[key] ? 'Available' : 'Not available'}</dd></React.Fragment>)}
      </dl>
      {!capabilities.worker && <p role="alert">This browser cannot start background calculations. Open the lab in a browser that supports background calculations.</p>}
    </section>
    <form style={panel} onSubmit={start} aria-label="Synthetic test inputs">
      <h2>Try a test definition</h2>
      <label>Simulated test<select style={field} value={definitionId} onChange={event => selectDefinition(event.target.value)}>
        {TEST_DEFINITIONS.map(item => <option key={item.id} value={item.id}>{item.label}</option>)}
      </select></label>
      <p>{definition.description}</p><p style={muted}>{definition.category} · Definition version {definition.version}</p>
      <fieldset disabled={running} style={{ border: 0, padding: 0, margin: '1rem 0', display: 'grid', gap: '.8rem' }}>
        <legend>Synthetic inputs</legend>
        {definition.inputs.map(input => input.type === 'boolean' ? <label key={input.key} style={{ display: 'flex', alignItems: 'start', gap: '.5rem' }}>
          <input type="checkbox" checked={inputs[input.key] === true} onChange={event => updateInput(input.key, event.target.checked)} />{input.label}
        </label> : <label key={input.key}>{input.label}{input.unit ? ` (${input.unit})` : ''}
          <input type="number" style={field} value={inputs[input.key] ?? ''} min={input.min} max={input.max} step={input.integer ? 1 : 'any'} required
            onChange={event => updateInput(input.key, event.target.value)} />
        </label>)}
      </fieldset>
      <div style={{ display: 'flex', gap: '.7rem', flexWrap: 'wrap' }}>
        <button className="ni-button ni-button--primary" type="submit" disabled={running || !capabilities.worker}>Run simulation</button>
        {running && <button className="ni-button" type="button" onClick={cancel}>Cancel calculation</button>}
      </div>
    </form>
    <section style={panel} aria-label="Page response check"><h2>Page response check</h2>
      <p style={muted}>Your browser runs calculations separately so this page stays responsive. Use this counter while a calculation runs to check the page response.</p>
      <button className="ni-button" type="button" onClick={() => { setResponseChecks(value => value + 1); if (running) setChecksDuringRun(value => value + 1); }}>Check page response</button>
      <p>Response checks: {responseChecks} · During this calculation: {checksDuringRun}</p>
    </section>
    {running && <p role="status">Calculating with synthetic data… You can keep using the response counter.</p>}
    {status === 'cancelled' && <p role="status">Calculation cancelled. No result was retained.</p>}
    {elapsed !== null && <p style={muted}>Observed elapsed time: {elapsed.toFixed(1)} ms. Includes starting the calculation and receiving its result on this browser; this is not a benchmark.</p>}
    {error && <p role="alert">{error}</p>}
    {result && <ResultView result={result} />}
  </div>;
}
